import * as React from "react";

import { makeStyles, createStyles } from "@material-ui/core";
import classnames from "classnames";
import {
    baseColors,
    RelodeTheme,
    Text,
    Button,
    SelectableButtonYesNoOption,
} from "..";
import { CheckCircleIcon } from "../../assets/icons";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                display: "flex",
                justifyContent: "space-between",
            },
            largeButton: {
                minHeight: 71,
                minWidth: "48%",
                padding: 15,
                "&:disabled": {
                    color: baseColors.inactive_gray,
                    backgroundColor: baseColors.cool_gray,
                },
                margin: theme.spacing(0, 1),
                color: baseColors.gray,
            },
            selectableButtonLabelText: {
                fontSize: 16,
                [theme.breakpoints.down("md")]: {
                    fontSize: 12,
                    lineHeight: 1,
                },
            },
            selectedButtonLabelTextAlt: {
                color: baseColors.purple_alt,
            },
            selectedLargeButton: {
                border: `1px solid ${baseColors.purple_alt}`,
            },
            selectedLargeButtonAlt: {
                border: `1px solid ${baseColors.purple_alt}`,
                backgroundColor: baseColors.background_purple,
                color: baseColors.purple_alt,
            },
            selectableButtonFlex: {
                display: "flex",
                flexDirection: "row",
                width: "100%",
                alignItems: "center",
                justifyContent: "space-evenly",
            },
            selectableButtonLabel: {
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                justifyContent: "center",
                flexBasis: "95%",
                paddingRight: 25,
            },
            selectableButtonCheckbox: {
                width: 25,
                height: 25,
                color: baseColors.purple_alt,
            },
            selectableButtonSubtitle: {
                fontSize: 16,
                [theme.breakpoints.down("md")]: {
                    fontSize: 12,
                    lineHeight: 1,
                },
            },
        }),
    { name: "selectableButtonsYesNo" }
);

export interface SelectableButtonsYesNoProps {
    id: string;
    yesOption: SelectableButtonYesNoOption;
    noOption: SelectableButtonYesNoOption;
    selectedOption: SelectableButtonYesNoOption;
    onChange: (option: SelectableButtonYesNoOption) => void;
}

export const SelectableButtonsYesNo = (props: SelectableButtonsYesNoProps) => {
    const { id, yesOption, noOption, selectedOption, onChange } = props;
    const classes = useStyles({});

    const options = [yesOption, noOption];

    return (
        <div className={classes.root}>
            {options.map(option => {
                const isOptionSelected = option === selectedOption;

                const className = classnames(classes.largeButton, {
                    [classes.selectedLargeButtonAlt]:
                        isOptionSelected && option === yesOption,
                    [classes.selectedLargeButton]:
                        isOptionSelected && option === noOption,
                });
                const labelTextClassName = classnames(
                    classes.selectableButtonLabelText,
                    {
                        [classes.selectedButtonLabelTextAlt]:
                            isOptionSelected && option === yesOption,
                    }
                );

                return (
                    <Button
                        data-cy={`${id}-${option.name}-button`}
                        data-cy-value={
                            isOptionSelected ? "selected" : "unselected"
                        }
                        id={`${id}-${option.name}`}
                        key={`${id}-${option.name}-${option.id}`}
                        variant={isOptionSelected ? "outlined" : "contained"}
                        onClick={() => onChange(option)}
                        className={className}
                    >
                        <div className={classes.selectableButtonFlex}>
                            <div className={classes.selectableButtonCheckbox}>
                                {isOptionSelected ? (
                                    <CheckCircleIcon fontSize="small"></CheckCircleIcon>
                                ) : null}
                            </div>
                            <div className={classes.selectableButtonLabel}>
                                <Text
                                    variant="h2"
                                    className={labelTextClassName}
                                >
                                    {option.label}
                                </Text>
                                <Text
                                    variant="subtitle1"
                                    className={classes.selectableButtonSubtitle}
                                >
                                    {option.description}
                                </Text>
                            </div>
                        </div>
                    </Button>
                );
            })}
        </div>
    );
};
